import { Button, Card, Icon, message, Spin } from 'antd';
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { RespondToReferral } from '../../../../GraphQL';
import { ReferralData, QUERY } from './GetReferralQuery';
import {
  LIST_AUDIT_LOG_QUERY,
  ListAuditLogsQueryData,
  ListAuditLogsVariables,
  AuditLogEntry,
} from './ListAuditLogQuery';
import { deletePaginatedCacheItems } from '../../../../helpers/cache';
import { DeclineReferralModal } from './DeclineReferralModal';

interface Props {
  referral: ReferralData;
  organizationId: string;
}

const getLatestReceiverResponse = (entries: AuditLogEntry[]): string | null => {
  const receiverEntries = entries.filter(e => e.action === 'referral.receiver_response');
  if (!receiverEntries.length) return null;
  const latest = receiverEntries[0];
  const responseCtx = latest.recordContext.find(c => c.key === 'response');
  return responseCtx?.value ?? null;
};

export const ReceiverResponseButtons: FC<Props> = ({ referral, organizationId }): JSX.Element => {
  const { data, loading: queryLoading } = useQuery<ListAuditLogsQueryData, ListAuditLogsVariables>(
    LIST_AUDIT_LOG_QUERY,
    {
      variables: {
        action: 'referral.receiver_response',
        recordId: referral.id,
        organizationId,
      },
    }
  );

  const latestResponse = data?.listAuditLogs ? getLatestReceiverResponse(data.listAuditLogs.entries) : null;
  const isAccepted = latestResponse === 'accepted';
  const isDeclined = latestResponse === 'declined';
  const hasResponded = latestResponse !== null;

  const [declineModalVisible, setDeclineModalVisible] = useState(false);

  const [respondToReferral, { loading: acceptLoading }] = useMutation(RespondToReferral);

  const handleAccept = (): void => {
    respondToReferral({
      variables: { referralId: referral.id, action: 'ACCEPTED' },
      refetchQueries: [
        { query: QUERY, variables: { id: referral.id, organizationId } },
        {
          query: LIST_AUDIT_LOG_QUERY,
          variables: {
            action: 'referral.receiver_response',
            recordId: referral.id,
            organizationId,
          },
        },
      ],
      update: (cache: any) => {
        message.success('Receiver response: Accepted');
        deletePaginatedCacheItems(cache, 'ListReferrals');
      },
      onError: () => {
        message.error('Failed to accept referral');
      },
    });
  };

  const statusColor = isAccepted ? '#52c41a' : isDeclined ? '#f5222d' : '#1890ff';
  const statusBg = isAccepted ? '#f6ffed' : isDeclined ? '#fff1f0' : '#e6f7ff';
  const statusBorder = isAccepted ? '#b7eb8f' : isDeclined ? '#ffa39e' : '#91d5ff';
  const statusIcon = isAccepted ? 'check-circle' : isDeclined ? 'close-circle' : 'question-circle';
  const statusText = isAccepted ? 'Accepted' : isDeclined ? 'Declined' : 'Awaiting Response';

  if (queryLoading) {
    return (
      <Card
        size="small"
        style={{ width: '100%', background: '#e6f7ff', borderColor: '#91d5ff' }}
        bodyStyle={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 16px' }}
      >
        <Spin size="small" />
      </Card>
    );
  }

  return (
    <>
      <Card
        size="small"
        style={{
          width: '100%',
          background: statusBg,
          borderColor: statusBorder,
        }}
        bodyStyle={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          padding: '10px 16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon type={statusIcon} style={{ fontSize: 18, color: statusColor }} />
          <span style={{ fontWeight: 600, fontSize: 14, color: statusColor }}>Receiver Response: {statusText}</span>
          {hasResponded && <span style={{ color: '#8c8c8c', fontSize: 12 }}>(click to change)</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isAccepted && (
            <Button icon="check" onClick={handleAccept} loading={acceptLoading} disabled={acceptLoading}>
              Accept
            </Button>
          )}
          {!isDeclined && (
            <Button icon="close" onClick={() => setDeclineModalVisible(true)} disabled={acceptLoading}>
              Decline
            </Button>
          )}
        </div>
      </Card>
      <DeclineReferralModal
        referral={referral}
        organizationId={organizationId}
        visible={declineModalVisible}
        onClose={() => setDeclineModalVisible(false)}
      />
    </>
  );
};
